import { isBefore } from 'date-fns';
import { getProgramEventEndDate } from 'models';
import { useTranslation } from 'react-i18next';
import {
  Col,
  type DatePickerDateType,
  DateTimeInput,
  type DateTimeInputItemProperties,
  type FormInstance,
  Row,
  SelectInput,
  getDatePickerDateType,
  getDateType,
} from 'ui';

type ItemP = {
  name: string;
} & Omit<DateTimeInputItemProperties, 'name'>;

type P = {
  disabledDuration?: boolean;
  disabledTime?: boolean;
  duration: ItemP;
  durations?: number[];
  end: ItemP;
  form: FormInstance;
  maximum?: Date;
  minimum: Date;
  mode?: 'create' | 'duplicate' | 'edit';
  start: ItemP;
};

const defaultOptions = [30, 60, 90, 120, 150, 180, 210, 240];

const EventTime = ({
  disabledDuration,
  disabledTime,
  duration,
  durations = defaultOptions,
  end,
  form,
  maximum,
  minimum,
  mode = 'create',
  start,
}: P) => {
  const { t } = useTranslation();

  const findClosesValue = (value?: number) => {
    if (value === undefined) {
      return {
        value: undefined,
      };
    }

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- TODO: fix this
    let closest = durations[0]!;

    for (const anotherDuration of durations) {
      const closestDiff = Math.abs(closest - value);
      const currentDiff = Math.abs(anotherDuration - value);

      if (currentDiff < closestDiff || (currentDiff === closestDiff && anotherDuration > closest)) {
        closest = anotherDuration;
      }
    }

    return {
      value: closest,
    };
  };

  const formatDurationLabel = (durationInMinutes: number) => {
    const hours = Math.floor(durationInMinutes / 60);
    const minutes = durationInMinutes % 60;

    // eslint-disable-next-line sonarjs/no-nested-conditional -- TODO: fix later
    const formattedHours = hours > 0 ? `${hours} ${hours === 1 ? t('hour') : t('hours')}` : null;

    // eslint-disable-next-line sonarjs/no-nested-conditional -- TODO: fix later
    const formattedMinutes = minutes > 0 ? `${minutes} ${minutes === 1 ? t('minute') : t('minutes')}` : null;
    return `${formattedHours ?? ''} ${formattedMinutes ?? ''}`.trim();
  };

  function generateOptions(values: number[]) {
    return values.map((durationValue) => ({
      label: formatDurationLabel(durationValue),
      value: durationValue,
    }));
  }

  const updateTime = (eventStart?: DatePickerDateType | null, eventDuration?: number) => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
    const currentStart = eventStart ?? (form.getFieldValue(start.name) as DatePickerDateType);
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
    const currentDuration = eventDuration ?? (form.getFieldValue(duration.name) as number);

    form.setFieldsValue({
      [end.name]: getDatePickerDateType(
        getProgramEventEndDate({ duration: currentDuration, startTime: getDateType(currentStart) }),
      ),
      [start.name]: currentStart,
    });
  };

  return (
    <>
      <Row>
        <Col span={13}>
          <DateTimeInput
            input={{
              'data-test-id': 'event_start-time',
              disabled: disabledTime,
              maximum,
              minimum,
              onChange(date) {
                updateTime(date);
              },
              showTime: true,
            }}
            item={{
              rules:
                mode === 'edit'
                  ? undefined
                  : [
                      {
                        message: t('Error: Date and time cannot be in the past'),
                        // eslint-disable-next-line @typescript-eslint/require-await -- Antd interface need async validator
                        async validator(_, value?: Date) {
                          if (value && isBefore(value, new Date())) {
                            throw new Error('Error: Date and time cannot be in the past');
                          }
                        },
                      },
                      {
                        required: true,
                      },
                    ],

              ...start,
            }}
          />
        </Col>
        <Col offset={1} span={10}>
          <SelectInput
            input={{
              disabled: disabledDuration,
              getOptionLabel: (option) => option.label,
              getOptionValue: (option) => option.value,
              // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
              onChange(value: any) {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-argument -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
                updateTime(null, value);
              },
              options: generateOptions(durations),
            }}
            item={{
              ...duration,
              'data-test-id': 'event_duration',
              getValueProps: findClosesValue,
              rules: [{ required: true }],
            }}
          />
        </Col>
      </Row>
      <DateTimeInput input={{ showTime: true }} item={{ ...end, hidden: true }} />
    </>
  );
};

export default EventTime;
